import { Center, VerticalCenter } from "@/common/css";
import { ActivityCardSkeleton, DashboardSkeleton } from "@/common/shimmer/Shimmer";

import { Image } from "@/common/ui/Image";
import { H1, H2, LightText2 } from "@/common/ui/Text";
import EmptyList from "@/components/common/EmptyList";
import UpdateTempPassword from "@/components/dashboard/modal/UpdateTempPassword";
import { Button } from "@/components/ui/button";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import { useDashboardInfo } from "@/services/dashboard.services";
import { isOrgPending, loginSelector } from "@/store/features/loginSlice";
import { PROFILE_STATUS } from "@/utils/constant/global.constant";
import moment from "moment";
import React, { useEffect, memo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CommonButton from "@/common/ui/Button";
import { dashboardSelector } from "@/store/features/dashboardSlice";

const ProfileActivityCard = (props: any) => {
  // const navigate = useNavigate();
  const { description, dateTime } = props;

  return (
    <div className="shadow-sm bg-black/5 flex gap-1 items-center rounded-md px-4">
      <Image src="/icons/network.svg" alt="" className="size-12" />
      <div className="py-19 flex flex-col gap-1 w-full">
        <Center className="justify-between w-full">
          <div className="flex flex-col">
            <LightText2 text={description} />
            <LightText2 text={moment(dateTime).format("DD MMM YYYY, hh:mm A")} className="!text-text-light2" />
          </div>
          {/* <Button variant="ghost" className="text-13 text-text-primary " onClick={() => navigate("/")}>
            View details
          </Button> */}
        </Center>
      </div>
    </div>
  );
};

const DashboardCard = ({ count, label, status }: any) => {
  return (
    <div className=" flex gap-1 items-center rounded-md px-4 w-full h-[150px] bg-white justify-center relative">
      {status && <LightText2 text={label} className="!text-warning !text-10 absolute top-2 right-2 p-1 px-3  bg-warningBg rounded-md" />}
      <VerticalCenter className=" gap-y-2">
        <H1 text={count} />
        <LightText2 text={label} className=" text-center" />
      </VerticalCenter>
    </div>
  );
};

export const SetupProfileModal = ({ onVerify, onClose }: any) => {
  const { isSideBarExpanded } = useSelector(dashboardSelector);
  const { currentOrg } = useSelector(loginSelector);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed right-0 top-0 bottom-0 bg-black/30 flex items-center justify-center z-50 ${
          isSideBarExpanded ? "w-full md:w-[calc(100%-200px)]" : "w-full md:w-[calc(100%-48px)]"
        }`}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center"
        >
          <div className="flex flex-col items-center">
            {/* Image */}
            <img src="/icons/NoDocuments.svg" alt="Setup Profile" className="w-28 h-28" />

            {/* Heading */}
            <h2 className="text-base  leading-normal font-semibold text-text-default">Complete Setup</h2>

            {/* Description */}
            <p className="text-sm text-gray-500 pt-2">To start adding employees, please complete your organization setup and get your document verified.</p>

            {/* Buttons */}
            <div className="w-full mt-4 flex items-center flex-col gap-2">
              <CommonButton variant="primary" size="small" onClick={onVerify}>
                Complete Setup
              </CommonButton>
              <button
                onClick={onClose}
                disabled={!currentOrg?.SignupOrg?.org_name || !onClose}
                className="text-sm text-gray-400 underline hover:text-gray-600 disabled:cursor-not-allowed"
              >
                I’ll do it later
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
const ProfileActivities = memo(() => {
  const [localData, setLocalData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const { isLoading, data } = useDashboardInfo({
    key: "profile-activities",
    page,
    per_page: 10,
  });

  const hasMore = data?.count > localData.length;

  useEffect(() => {
    if (data && data?.rows?.length > 0) {
      setLocalData((prevData) => [...prevData, ...data.rows]);
    }
  }, [data]);

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const lastItemRef = useRef<HTMLDivElement | null>(null);

  // Use IntersectionObserver to trigger loadMore when last item comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 1.0 }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (lastItemRef.current) {
        observer.unobserve(lastItemRef.current);
      }
    };
  }, [hasMore, isLoading]);

  return (
    <div className="bg-white  px-2 md:px-4 w-[calc(100%-20px)] md:w-[calc(100%-40px)] rounded-md">
      <H2 text="Profile activities" className="pb-5 py-5 sticky -top-5 bg-white" />
      <div className="flex flex-col gap-y-4 pb-5">
        {isLoading && localData.length === 0 ? (
          <div className="flex flex-col gap-y-4">
            {[...Array(5)].map((_, i) => (
              <ActivityCardSkeleton key={i} />
            ))}
          </div>
        ) : localData.length > 0 ? (
          <>
            {localData.map((activity: any, index: number) => (
              <ProfileActivityCard key={index} {...activity} />
            ))}
            {isLoading && (
              <div className="flex flex-col gap-y-4">
                {[...Array(2)].map((_, i) => (
                  <ActivityCardSkeleton key={i} />
                ))}
              </div>
            )}
          </>
        ) : (
          <EmptyList text="No activities yet" icon="/icons/empty-inbox.svg" textClassName="!font-bold" className="h-full gap-y-4 mt-10" />
        )}

        {/* Last item to trigger the loadMore function */}
        {hasMore && !isLoading && <div ref={lastItemRef} className="h-1"></div>}
      </div>
    </div>
  );
});

const modalConfig: ModalConfig = {
  updateTempPassword: {
    component: UpdateTempPassword,
    title: "Set new password",
    modalClassName: "!max-w-[330px]",
  },
};

const DashboardPage: React.FC<any> = ({ openModal }) => {
  const navigate = useNavigate();
  const { currentOrg, currentUser } = useSelector(loginSelector);
  const isOrgPendingStatus = useSelector(isOrgPending);
  const { isLoading, data } = useDashboardInfo({});
  const [showProfileSetup, setProfileSetup] = useState(false);

  const OnclickToOrganizationProfile = () => {
    navigate("/home/settings/organization-profile");
  };

  useEffect(() => {
    if (currentUser?.isTempPass === "true") {
      openModal("updateTempPassword");
    }
  }, [currentUser]);

  useEffect(() => {
    if (isOrgPendingStatus) {
      setProfileSetup(true);
    }
  }, [isOrgPendingStatus]);

  return (
    <>
      <div className=" bg-containerBg w-full h-full py-3 md:py-5 overflow-y-auto">
        <div className="flex flex-col gap-3 md:gap-5 items-center ">
          {currentOrg?.SignupOrg?.acc_sts_id !== PROFILE_STATUS["PRFL-CMTD"] && (
            <div className=" flex items-center justify-between w-[calc(100%-40px)] bg-white ">
              <div className="rounded-md flex items-center gap-4 px-3 md:px-4">
                <Image src="/icons/network.svg" alt="" />
                <LightText2 text="Complete your profile and get your document approved to access all features." className=" !text-10 md:!text-13 py-19 !font-bold" />
              </div>
              <Button variant="ghost" className="text-13 text-text-link2 " onClick={OnclickToOrganizationProfile}>
                Get Started
              </Button>
            </div>
          )}
          {isLoading ? (
            <DashboardSkeleton />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 w-full px-2 md:px-5">
              {data.map((card: any, index: number) => (
                <DashboardCard key={index} {...card} />
              ))}
            </div>
          )}
          {/* activities */}
          <ProfileActivities />
        </div>
      </div>
      {showProfileSetup && <SetupProfileModal onVerify={OnclickToOrganizationProfile} onClose={() => setProfileSetup(false)} />}
    </>
  );
};

export const DashboardPageWithModal = withModal(DashboardPage, modalConfig);
